import React, { useLayoutEffect, useRef, useState } from "react";
import Container from "../components/Container";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import faroLogo from "../assets/faro.png";

/* ─── Palette ─── */
const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  navyDeep: "#06122A",
  amber: "#FFC857",
  amberDark: "#E6B04E",
  paper: "#F5F2EA",
  paperSoft: "#FAF7EE",
  bone: "#EDE7D7",
  white: "#F5F7FA",
  gray: "#6B7280",
  grayLight: "#9CA3AF",
  grayDark: "#4B5563",
  ink: "#0F172A",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

/* ─── Brochure primitives ─── */
function AmberSidebar({ opacity = 0.15 }) {
  return <div className="absolute top-0 right-0 w-2 md:w-3 h-full pointer-events-none"
    style={{ background: C.amber, opacity }} />;
}
function AmberRule({ width = "52px", className = "" }) {
  return <div className={`h-0.5 rounded-full ${className}`} style={{ width, background: C.amber }} />;
}
function FaroWatermark({ className = "", position = "left", opacity = 0.04 }) {
  return (
    <div className={`absolute pointer-events-none select-none ${className}`}
      style={{ transform: position === "right" ? "scaleX(-1)" : "none" }}>
      <img src={faroLogo} alt="" className="w-full h-full object-contain"
        style={{ opacity, filter: "brightness(2)" }} draggable={false} />
    </div>
  );
}
function Kicker({ children, onDark = true }) {
  // On dark navy → amber text on translucent amber chip
  // On light paper → navy text on white chip with amber dot (alto contraste)
  const styles = onDark
    ? {
        color: C.amber,
        background: "rgba(255,200,87,0.12)",
        border: `1px solid rgba(255,200,87,0.4)`,
        dotColor: C.amber,
      }
    : {
        color: C.navy,
        background: C.white,
        border: `1px solid rgba(11,31,58,0.18)`,
        dotColor: C.amberDark,
      };
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] px-3.5 py-1.5 rounded-full mb-4"
      style={{
        color: styles.color,
        background: styles.background,
        border: styles.border,
        fontFamily: MONO,
        boxShadow: onDark ? "none" : "0 1px 3px rgba(11,31,58,0.06)",
      }}>
      <span className="w-1.5 h-1.5 rounded-full"
        style={{ background: styles.dotColor, boxShadow: `0 0 8px ${styles.dotColor}` }} />
      {children}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   01 · HERO
   Beam anclado al centro real del logo (ref + ResizeObserver)
   ═══════════════════════════════════════════════════════════ */
function Hero({ onNavigate }) {
  const sectionRef = useRef(null);
  const logoRef = useRef(null);
  const [origin, setOrigin] = useState({ x: 0.75, y: 0.5 });

  // Cursor tracking — solo rota el haz, NO modula intensidad
  const mx = useMotionValue(0.4);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 55, damping: 20 });
  const sy = useSpring(my, { stiffness: 55, damping: 20 });
  const beamRotate = useTransform([sx, sy], ([x, y]) => {
    const dx = x - origin.x, dy = y - origin.y;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  });

  useLayoutEffect(() => {
    const update = () => {
      const s = sectionRef.current?.getBoundingClientRect();
      const l = logoRef.current?.getBoundingClientRect();
      if (!s || !l || s.width === 0) return;
      setOrigin({
        x: (l.left + l.width / 2 - s.left) / s.width,
        y: (l.top + l.height / 2 - s.top) / s.height,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    if (sectionRef.current) ro.observe(sectionRef.current);
    if (logoRef.current) ro.observe(logoRef.current);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const onMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.25); my.set(0.5); };

  const originLeft = `${origin.x * 100}%`;
  const originTop = `${origin.y * 100}%`;

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative overflow-hidden min-h-screen h-screen flex items-center"
      style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}
    >
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
      <AmberSidebar />
      <FaroWatermark className="top-0 right-0 w-[60%] h-full" position="right" />

      {/* Outer halo — capa amplia más suave */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: 720, height: 720,
          left: originLeft, top: originTop,
          marginLeft: -360, marginTop: -360,
          background: "radial-gradient(circle, rgba(255,200,87,0.20) 0%, rgba(255,200,87,0.06) 40%, transparent 70%)",
          filter: "blur(28px)",
        }}
      />

      {/* Inner core glow — más intenso, abraza el logo */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: 420, height: 420,
          left: originLeft, top: originTop,
          marginLeft: -210, marginTop: -210,
          background: "radial-gradient(circle, rgba(255,200,87,0.55) 0%, rgba(255,200,87,0.22) 25%, rgba(255,200,87,0.06) 50%, transparent 70%)",
          filter: "blur(22px)",
        }}
      />

      {/* Bright nucleus — el corazón de luz */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: 180, height: 180,
          left: originLeft, top: originTop,
          marginLeft: -90, marginTop: -90,
          background: "radial-gradient(circle, rgba(255,220,140,0.55) 0%, rgba(255,200,87,0.25) 40%, transparent 70%)",
          filter: "blur(14px)",
        }}
      />

      <Container className="relative z-10 py-20 md:py-24">
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
          <div className="space-y-7 max-w-xl">
            <motion.h1 {...fadeUp(0.08)}
              className="text-[34px] md:text-[44px] lg:text-[52px] leading-[1.08] tracking-tight"
              style={{ fontFamily: SANS, color: C.white, fontWeight: 700, letterSpacing: "-0.015em" }}>
              Una plataforma que convierte tus datos en
              <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}> decisiones óptimas</span>.
            </motion.h1>

            <motion.p {...fadeUp(0.16)} className="text-[15px] md:text-base leading-[1.7] max-w-lg"
              style={{ color: C.white, opacity: 0.92, fontFamily: SANS, fontWeight: 500 }}>
              Alimentada por modelos que calculan la decisión óptima en cada punto
              clave de tu operación: aprenden de tus datos, anticipan el contexto y
              resuelven bajo las restricciones de tu negocio. Decisiones que se
              ejecutan automáticamente y se traducen en más margen, a escala.
            </motion.p>

            <motion.div {...fadeUp(0.24)} className="flex flex-wrap gap-3 pt-1">
              <button onClick={() => onNavigate("contact")}
                className="rounded-xl text-sm md:text-base px-6 py-3 font-bold transition-all hover:-translate-y-0.5"
                style={{ background: C.amber, color: C.navy, fontFamily: SANS, boxShadow: "0 4px 24px rgba(255,200,87,0.28)" }}>
                Solicitar demo →
              </button>
            </motion.div>
          </div>

          <div className="hidden md:flex justify-center">
            <motion.div {...fadeUp(0.15)} ref={logoRef}
              className="relative flex items-center justify-center aspect-square w-full max-w-[520px] mx-auto">

              {/* ambient research / data science layer */}
              <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.55 }}>
                {/* slow rotating orbit 1 */}
                <motion.g
                  animate={{ rotate: 360 }}
                  transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "200px 200px" }}>
                  <circle cx="200" cy="200" r="170" fill="none" stroke="rgba(255,200,87,0.18)" strokeWidth="0.8" strokeDasharray="2 6" />
                  <circle cx="200" cy="30" r="3" fill="#FFC857" opacity="0.7" />
                  <circle cx="370" cy="200" r="2" fill="#FFC857" opacity="0.5" />
                  <circle cx="200" cy="370" r="2.5" fill="#FFC857" opacity="0.6" />
                </motion.g>
                <motion.g
                  animate={{ rotate: -360 }}
                  transition={{ duration: 48, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "200px 200px" }}>
                  <circle cx="200" cy="200" r="140" fill="none" stroke="rgba(255,200,87,0.12)" strokeWidth="0.6" strokeDasharray="1 4" />
                  <circle cx="60" cy="200" r="2.5" fill="#FFC857" opacity="0.55" />
                  <circle cx="200" cy="60" r="2" fill="#FFC857" opacity="0.4" />
                </motion.g>

              </svg>

              {/* logo on top */}
              <motion.div
                animate={{ opacity: [0.92, 1, 0.92] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10"
                style={{ transform: "scaleX(-1)" }}>
                <img src={faroLogo} alt="FARO"
                  className="w-[72%] max-w-[340px] h-auto mx-auto"
                  style={{ filter: "drop-shadow(0 0 28px rgba(255,200,87,0.45))" }} />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   02 · PROBLEMA
   ═══════════════════════════════════════════════════════════ */
function ProblemSection({ onNavigate }) {
  return (
    <section className="relative py-20 md:py-24 overflow-hidden" style={{ background: C.paper }}>
      {/* paper grain */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
        backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
        backgroundSize: "3px 3px",
      }} />
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.6 }} />

      <Container className="relative z-10">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="text-[28px] md:text-[34px] lg:text-[40px] leading-[1.15] font-bold"
            style={{ color: C.navy, fontFamily: SANS, letterSpacing: "-0.015em" }}>
            ¿Por qué{" "}
            <span style={{ color: C.amberDark, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>
              FARO
            </span>?
          </h2>
          <AmberRule className="mt-5 mx-auto" />
        </div>

        {/* Dos tarjetas grandes */}
        <div className="grid gap-6 md:grid-cols-2 max-w-6xl mx-auto items-stretch">
          {/* Card 1 — Por qué modelos */}
          <motion.div {...fadeUp(0)}
            className="rounded-2xl p-8 md:p-10 flex flex-col"
            style={{
              background: C.white,
              border: `1px solid rgba(11,31,58,0.08)`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 10px 30px rgba(11,31,58,0.06)",
            }}>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: C.amberDark, fontFamily: MONO }}>
              Inteligencia
            </span>
            <h3 className="mt-3 text-[22px] md:text-[26px] font-bold leading-[1.2]" style={{ color: C.navy, fontFamily: SANS }}>
              Los grandes retailers ya deciden con modelos inteligentes.
            </h3>
            <div className="w-12 h-px my-5" style={{ background: C.amber }} />
            <p className="text-[15px] md:text-base leading-[1.75]" style={{ color: C.grayDark, fontFamily: SANS }}>
              Un amplio cuerpo de estudios confirma que las cadenas líderes del mundo
              utilizan modelos inteligentes para tomar decisiones óptimas y
              automatizadas. Para muchas, son hoy su principal ventaja competitiva.
            </p>
          </motion.div>

          {/* Card 2 — Quiénes somos */}
          <motion.div {...fadeUp(0.08)}
            className="rounded-2xl p-8 md:p-10 flex flex-col"
            style={{
              background: C.navy,
              border: `1px solid rgba(255,200,87,0.25)`,
              boxShadow: "0 10px 30px rgba(11,31,58,0.18)",
            }}>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: C.amber, fontFamily: MONO }}>
              Equipo
            </span>
            <h3 className="mt-3 text-[22px] md:text-[26px] font-bold leading-[1.2]" style={{ color: C.white, fontFamily: SANS }}>
              Detrás de FARO hay un equipo que entiende tu negocio.
            </h3>
            <div className="w-12 h-px my-5" style={{ background: C.amber }} />
            <p className="text-[14px] md:text-[15px] leading-[1.75]" style={{ color: C.grayLight, fontFamily: SANS }}>
              Especialistas en inteligencia artificial, ciencia de datos y desarrollo
              de software con experiencia real en varias industrias. Cada modelo y
              cada decisión técnica está respaldada por quienes entienden tanto la
              tecnología como el problema que resuelve.
            </p>
            <button onClick={() => onNavigate("about")}
              className="mt-auto pt-6 inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.18em] self-start transition-transform hover:translate-x-0.5"
              style={{ color: C.amber, fontFamily: MONO }}>
              Conocer al equipo
              <span>→</span>
            </button>
          </motion.div>
        </div>

        {/* CTA — Próximo paso */}
        <motion.div {...fadeUp(0.16)}
          className="mt-6 max-w-6xl mx-auto rounded-2xl p-8 md:p-12 text-center"
          style={{
            background: `linear-gradient(135deg, ${C.navyLight} 0%, ${C.navy} 100%)`,
            border: `1px solid rgba(255,200,87,0.25)`,
            boxShadow: `0 20px 60px rgba(0,0,0,0.2), 0 0 80px rgba(255,200,87,0.05)`,
          }}>
          <span className="inline-block text-[11px] md:text-[12px] font-bold uppercase tracking-[0.32em] mb-4"
            style={{ color: C.amber, fontFamily: MONO }}>
            Próximo paso
          </span>
          <h3 className="text-[26px] md:text-[34px] font-bold leading-[1.2] mb-4"
            style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
            Probá FARO sobre{" "}
            <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>tus operaciones</span>.
          </h3>
          <p className="text-[14px] md:text-[15px] leading-[1.55] max-w-xl mx-auto mb-8" style={{ color: C.grayLight }}>
            Una gran oportunidad para cuantificar el impacto en tu operación, sin costo.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => onNavigate("contact")}
              className="rounded-xl text-sm md:text-base px-7 py-3.5 font-bold transition-all hover:-translate-y-0.5"
              style={{ background: C.amber, color: C.navy, boxShadow: "0 4px 24px rgba(255,200,87,0.35)", fontFamily: SANS }}>
              Solicitar demo →
            </button>
          </div>
        </motion.div>
      </Container>

    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   03 · SOLUCIÓN — Supply Chain interactiva
   Click en una etapa → desplegable con productos
   ═══════════════════════════════════════════════════════════ */

const Icon = ({ d, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const ICONS = {
  factory: <><path d="M3 21V10l6 4V10l6 4V6l6 4v11z" /><path d="M3 21h18" /></>,
  warehouse: <><path d="M3 21V9l9-5 9 5v12" /><path d="M9 21v-7h6v7" /></>,
  store: <><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v12h16V9" /><path d="M9 21v-6h6v6" /></>,
  customer: <><circle cx="12" cy="8" r="3.5" /><path d="M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7" /></>,
  forecast: <><path d="M3 17l5-5 4 3 8-8" /><path d="M14 7h6v6" /></>,
  replen: <><rect x="3" y="8" width="13" height="10" rx="1" /><path d="M16 11h3l2 3v4h-5" /><circle cx="7.5" cy="19" r="1.6" /><circle cx="17.5" cy="19" r="1.6" /></>,
  pricing: <><path d="M20 12l-8 8-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.4" /></>,
  promo: <><circle cx="6.5" cy="6.5" r="1.5" /><circle cx="17.5" cy="17.5" r="1.5" /><path d="M19 5L5 19" /></>,
  stock: <><path d="M21 7L12 3 3 7l9 4 9-4z" /><path d="M3 7v10l9 4 9-4V7" /><path d="M12 11v10" /></>,
  surtido: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  inbound: <><path d="M4 12h12" /><path d="M12 6l6 6-6 6" /><path d="M20 4v16" /></>,
  understanding: <><circle cx="12" cy="12" r="8" /><path d="M8 12h8M12 8v8" /></>,
  truck: <><path d="M3 7h11v10H3z" /><path d="M14 10h4l3 3v4h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17.5" cy="18" r="1.6" /></>,
  raw: <><path d="M7 4h10l-1 6H8z" /><path d="M6 10h12l-1 10H7z" /><path d="M10 14v4M14 14v4" /></>,
};

/* Stages of the supply chain */
const STAGES = [
  { id: "fabrica",   label: "Fábrica",                 icon: ICONS.factory   },
  { id: "cd",        label: "Centro de Distribución",  icon: ICONS.warehouse },
  { id: "tienda",    label: "Tienda",                  icon: ICONS.store     },
  { id: "cliente",   label: "Cliente",                 icon: ICONS.customer  },
];

/*
  PRODUCTS — cada producto vive en uno o más ENLACES de la cadena.
    `links` → array de pares [a, b] de stages adyacentes que el modelo cubre.
              Ej: [["fabrica","cd"], ["cd","tienda"]] cubre ambos tramos upstream.
*/
const PRODUCTS = [
  {
    id: "demand",
    name: "Planificación de la demanda",
    desc: "Predice la demanda futura con precisión, la base para planificar mejor cada etapa de tu operación.",
    category: "Planificación", group: "stages", icon: ICONS.forecast,
  },
  {
    id: "replen",
    name: "RUMBO",
    desc: "Automatiza la reposición: calcula la cantidad óptima de mercadería a reponer a cada tienda, todos los días.",
    category: "Inventario", group: "stages", icon: ICONS.replen, flagship: true,
    route: "rumbo",
  },
  {
    id: "pricing",
    name: "Optimización de precios",
    desc: "Encuentra el precio que más margen deja sin perder ventas. Recupera dinero que se pierde por precios de memoria.",
    category: "Pricing & Promociones", group: "stages", icon: ICONS.pricing,
  },
  {
    id: "promo",
    name: "Planificación de promociones",
    desc: "Diseña campañas que venden más, y mide si realmente sumaron o solo regalaron margen.",
    category: "Pricing & Promociones", group: "stages", icon: ICONS.promo,
  },
];

/* EndpointNode — Materia Prima / Cliente. No clickeable.
   highlight → dorado, para marcar que el foco final está ahí (Cliente). */
function EndpointNode({ label, icon, highlight = false }) {
  return (
    <div className="relative flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-xl w-full h-full"
      style={{
        background: highlight ? "rgba(255,200,87,0.14)" : C.white,
        border: highlight ? `1px solid ${C.amberDark}` : `1px dashed rgba(11,31,58,0.25)`,
        boxShadow: highlight ? `0 6px 20px rgba(224,169,46,0.22)` : "none",
        fontFamily: SANS,
      }}>
      {icon && (
        <span className="flex items-center justify-center"
          style={{ color: highlight ? C.amberDark : C.navy, opacity: highlight ? 1 : 0.55 }}>
          <Icon d={icon} size={22} />
        </span>
      )}
      <span className="text-[12px] md:text-[13px] font-bold leading-tight text-center"
        style={{ color: highlight ? C.navy : C.navy, opacity: highlight ? 1 : 0.6 }}>
        {label}
      </span>
    </div>
  );
}

/* ClickStage — chip clickeable con zona superior (ícono + label) y CTA inferior */
function ClickStage({ stage, active, onClick }) {
  return (
    <button onClick={onClick}
      className="relative flex flex-col items-stretch rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 w-full h-full group"
      style={{
        background: C.white,
        border: `1px solid ${active ? C.amberDark : "rgba(11,31,58,0.12)"}`,
        boxShadow: active
          ? `0 6px 20px rgba(224,169,46,0.22), 0 0 0 1px ${C.amberDark}`
          : "0 1px 2px rgba(11,31,58,0.04)",
        fontFamily: SANS,
      }}>
      {/* Zona superior — ícono + label, crece y empuja la CTA al fondo */}
      <div className="flex-1 flex flex-col items-center justify-center gap-1.5 px-3 pt-4 pb-3">
        <span className="flex items-center justify-center" style={{ color: active ? C.amberDark : C.navy }}>
          <Icon d={stage.icon} size={22} />
        </span>
        <span className="text-[12px] md:text-[13px] font-bold leading-tight text-center" style={{ color: C.navy }}>
          {stage.label}
        </span>
      </div>

      {/* CTA inferior — barra con fondo sutil */}
      <div className="px-3 py-2 text-center transition-colors"
        style={{
          background: active ? C.amber : "rgba(11,31,58,0.035)",
          borderTop: `1px solid ${active ? C.amberDark : "rgba(11,31,58,0.06)"}`,
        }}>
        <span className="text-[9.5px] font-bold uppercase tracking-[0.22em]"
          style={{ color: active ? C.navy : "rgba(11,31,58,0.55)", fontFamily: MONO }}>
          Ver soluciones
        </span>
      </div>
    </button>
  );
}

/* VConnector — línea vertical que se intensifica cuando su grupo está activo */
function VConnector({ active }) {
  return (
    <div className="flex justify-center" style={{ height: 28 }}>
      <span className="h-full transition-all" style={{
        width: active ? 2 : 1,
        background: C.amberDark,
        opacity: active ? 1 : 0.45,
        boxShadow: active ? `0 0 10px ${C.amber}` : "none",
      }} />
    </div>
  );
}

function SolutionSection({ onNavigate }) {
  // active = null | 'stages'
  const [active, setActive] = useState(null);

  const GROUPS = {
    stages: { label: "Inteligencia dentro de cada etapa" },
  };

  const products = !active ? [] : PRODUCTS.filter(p => p.group === active);
  const toggle = (g) => setActive(active === g ? null : g);
  const activeLabel = active ? GROUPS[active].label : "";

  return (
    <section className="relative py-20 md:py-24 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${C.navyMid} 0%, ${C.navy} 100%)` }}>
      <AmberSidebar />
      <FaroWatermark className="-top-10 -left-10 w-[40%] h-[120%]" position="left" />

      <Container className="relative z-10">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="text-[28px] md:text-[34px] lg:text-[40px] leading-[1.15] font-bold"
            style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
            Una plataforma.{" "}
            <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>Modelos</span>{" "}
            conectados a cada etapa de la cadena.
          </h2>
          <AmberRule className="mt-5 mx-auto" />
          <p className="mt-5 text-[15px] md:text-base leading-[1.7] max-w-xl mx-auto" style={{ color: C.grayLight, fontFamily: SANS }}>
            Modular por diseño: cada producto resuelve un problema específico y se
            ajusta a lo que cada operación necesita.
          </p>
        </div>

        {/* ───────────────────────────────────────────────
            DIAGRAMA — panel único; Materia Prima y Cliente ADENTRO,
            pero la plataforma solo abarca Fábrica → Tienda
            ─────────────────────────────────────────────── */}
        <motion.div {...fadeUp(0)}
          className="relative max-w-6xl mx-auto rounded-2xl p-6 md:p-10"
          style={{
            background: C.paper,
            border: `1px solid rgba(255,200,87,0.4)`,
            boxShadow: `0 20px 60px rgba(0,0,0,0.35), 0 0 80px rgba(255,200,87,0.06)`,
          }}>
          {/* paper grain inside panel */}
          <div className="absolute inset-0 rounded-2xl pointer-events-none opacity-[0.35]" style={{
            backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
            backgroundSize: "3px 3px",
          }} />

          <div className="relative">
            {/* Cadena header */}
            <div className="flex items-center gap-3 mb-4 px-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: C.amberDark, fontFamily: MONO }}>
                Cadena de suministro
              </span>
              <span className="flex-1 h-px" style={{ background: "rgba(11,31,58,0.15)" }} />
            </div>

            {/* DESKTOP — CSS Grid responsive: 5 chips (1fr) + 4 líneas (36px) */}
            <div className="hidden md:grid mx-auto relative w-full"
              style={{
                gridTemplateColumns: "minmax(0,1fr) 36px minmax(0,1fr) 36px minmax(0,1fr) 36px minmax(0,1fr) 36px minmax(0,1fr)",
                columnGap: "8px",
                rowGap: 0,
                maxWidth: 1050,
              }}>
              {/* ─── ROW 1: cadena ─── */}
              <EndpointNode label="Materia Prima" icon={ICONS.raw} />
              <div className="flex items-center justify-center">
                <span className="h-px w-full" style={{ background: C.amberDark, opacity: 0.55 }} />
              </div>
              <ClickStage stage={STAGES[0]} active={active === "stages"} onClick={() => toggle("stages")} />
              <div className="flex items-center justify-center">
                <span className="h-px w-full" style={{ background: C.amberDark, opacity: 0.55 }} />
              </div>
              <ClickStage stage={STAGES[1]} active={active === "stages"} onClick={() => toggle("stages")} />
              <div className="flex items-center justify-center">
                <span className="h-px w-full" style={{ background: C.amberDark, opacity: 0.55 }} />
              </div>
              <ClickStage stage={STAGES[2]} active={active === "stages"} onClick={() => toggle("stages")} />
              <div className="flex items-center justify-center">
                <span className="h-px w-full" style={{ background: C.amberDark, opacity: 0.55 }} />
              </div>
              <EndpointNode label="Cliente" icon={ICONS.customer} highlight />

              {/* ─── ROW 2: conectores verticales — bajo cada chip activable ─── */}
              <div />
              <div />
              <VConnector active={active === "stages"} />
              <div />
              <VConnector active={active === "stages"} />
              <div />
              <VConnector active={active === "stages"} />
              <div />
              <div />

              {/* ─── ROW 3: plataforma — full width al estar activa (cols 1–9), si no Fab→Tienda ─── */}
              <div style={{ gridColumn: active ? "1 / span 9" : "3 / span 5", transition: "all 0.4s ease" }}>
                <div className="relative rounded-xl overflow-hidden"
                  style={{
                    background: C.white,
                    border: `1px solid rgba(11,31,58,0.1)`,
                    boxShadow: `0 12px 40px rgba(11,31,58,0.12)`,
                  }}>
                {/* Productos arriba (cuando hay activo) */}
                <AnimatePresence initial={false}>
                  {active && (
                    <motion.div key={active}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                      className="overflow-hidden">
                      <div className="p-6 md:p-8" style={{ background: C.white }}>
                        <h3 className="text-xl md:text-2xl font-bold mb-5" style={{ color: C.navy, fontFamily: SANS }}>
                          {activeLabel}
                        </h3>

                        {products.length === 0 ? (
                          <p className="text-sm" style={{ color: C.grayDark }}>
                            Próximamente productos para esta etapa.
                          </p>
                        ) : (
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                            {Object.entries(
                              products.reduce((acc, p) => {
                                (acc[p.category] = acc[p.category] || []).push(p);
                                return acc;
                              }, {})
                            ).map(([cat, items]) => (
                              <div key={cat}>
                                <div className="text-[11px] font-bold uppercase tracking-[0.2em] pb-2 mb-3"
                                  style={{ color: C.amberDark, fontFamily: MONO, borderBottom: `1px solid rgba(224,169,46,0.35)` }}>
                                  {cat}
                                </div>
                                <ul className="flex flex-col gap-3">
                                  {items.map((p) => (
                                    <li key={p.id} className="flex flex-col">
                                      <div className="flex items-start gap-2.5">
                                        <span className="flex items-center justify-center mt-0.5 flex-shrink-0"
                                          style={{ color: C.amberDark }}>
                                          <Icon d={p.icon} size={16} />
                                        </span>
                                        <div className="flex-1">
                                          {p.route ? (
                                            <button onClick={() => onNavigate(p.route)}
                                              className="group/title inline-flex items-center gap-2 text-left"
                                              style={{ color: C.navy, fontFamily: SANS }}>
                                              <span className="text-[15px] font-extrabold leading-snug group-hover/title:underline"
                                                style={{ color: C.navy }}>
                                                {p.name}
                                              </span>
                                              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full transition-transform group-hover/title:translate-x-0.5"
                                                style={{ background: C.amber, color: C.navy, fontFamily: MONO }}>
                                                Ver producto →
                                              </span>
                                            </button>
                                          ) : (
                                            <span className="block text-[14px] font-semibold leading-snug"
                                              style={{ color: C.navy, fontFamily: SANS }}>
                                              {p.name}
                                            </span>
                                          )}
                                          {p.desc && (
                                            <span className="block mt-1.5 text-[12.5px] leading-[1.6]"
                                              style={{ color: C.grayDark, fontFamily: SANS }}>
                                              {p.desc}
                                            </span>
                                          )}
                                          <button onClick={() => onNavigate("contact")}
                                            className="mt-2.5 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full transition-all hover:-translate-y-0.5"
                                            style={{
                                              background: "rgba(255,200,87,0.15)",
                                              color: C.amberDark,
                                              border: `1px solid rgba(224,176,78,0.4)`,
                                              fontFamily: MONO,
                                            }}>
                                            Solicitar demo
                                            <span className="ml-0.5">→</span>
                                          </button>
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Barra de plataforma — siempre al fondo, ámbar sólido */}
                <div className="relative flex items-center justify-between py-5 md:py-6 px-6 md:px-10"
                  style={{
                    background: `linear-gradient(90deg, ${C.amber} 0%, ${C.amberDark} 50%, ${C.amber} 100%)`,
                    borderTop: active ? `1px solid rgba(11,31,58,0.08)` : "none",
                  }}>
                  <img src={faroLogo} alt="FARO" className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0"
                    style={{ filter: "drop-shadow(0 2px 6px rgba(11,31,58,0.25))" }} />
                  <div className="text-center flex-1">
                    <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.32em] mb-1"
                      style={{ color: C.navy, fontFamily: MONO, opacity: 0.7 }}>
                      Plataforma
                    </div>
                    <div className="text-2xl md:text-3xl font-extrabold tracking-[0.2em]"
                      style={{ color: C.navy, fontFamily: SERIF }}>
                      F · A · R · O
                    </div>
                  </div>
                  <img src={faroLogo} alt="" className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0"
                    style={{ filter: "drop-shadow(0 2px 6px rgba(11,31,58,0.25))", transform: "scaleX(-1)" }} />
                </div>
                {/* fin barra FARO */}
                </div>
                {/* fin rounded-xl platform */}
              </div>
              {/* fin gridColumn wrapper */}
            </div>
            {/* fin desktop grid */}

            {/* MOBILE — apilado vertical */}
            <div className="md:hidden flex flex-col gap-2">
              <div className="px-4 py-3 rounded-lg text-[13px] font-bold text-center"
                style={{ background: "transparent", border: `1px dashed rgba(11,31,58,0.25)`, color: C.navy, opacity: 0.65 }}>
                Materia Prima
              </div>
              <button onClick={() => toggle("stages")}
                className="px-4 py-3 rounded-lg text-[13px] font-bold transition-all text-left"
                style={{
                  background: active === "stages" ? "rgba(255,200,87,0.18)" : C.white,
                  border: `1px solid ${active === "stages" ? C.amberDark : "rgba(11,31,58,0.12)"}`,
                  color: C.navy,
                }}>
                Fábrica · Centro de Distribución · Tienda
              </button>
              <div className="px-4 py-3 rounded-lg text-[13px] font-bold text-center"
                style={{ background: "rgba(255,200,87,0.14)", border: `1px solid ${C.amberDark}`, color: C.navy }}>
                Cliente
              </div>
            </div>
          </div>
        </motion.div>

        {/* RUMBO spotlight */}
        <motion.div {...fadeUp(0.1)}
          className="mt-14 max-w-6xl mx-auto rounded-2xl overflow-hidden relative"
          style={{
            background: C.paper,
            border: `1px solid rgba(255,200,87,0.4)`,
            boxShadow: `0 20px 60px rgba(0,0,0,0.35), 0 0 80px rgba(255,200,87,0.06)`,
          }}>
          {/* paper grain inside panel */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
            backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
            backgroundSize: "3px 3px",
          }} />
          <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
          <div className="relative p-8 md:p-12 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
            <div className="flex-1">
              <h3 className="text-[40px] md:text-[56px] font-bold leading-none"
                style={{ color: C.navy, fontFamily: SERIF, letterSpacing: "0.02em" }}>
                RUMBO
              </h3>
              <p className="mt-2 text-[16px] md:text-[20px] italic leading-[1.3]"
                style={{ color: C.amberDark, fontFamily: SERIF, fontWeight: 500 }}>
                la reposición óptima de tu mercadería
              </p>
              <AmberRule width="100px" className="mt-5" />
              <p className="mt-5 text-[15px] md:text-[17px] leading-[1.6] max-w-xl" style={{ color: C.grayDark, fontFamily: SANS }}>
                RUMBO automatiza el cálculo de la{" "}
                <span style={{ fontWeight: 600, color: C.navy }}>cantidad óptima</span> a reponer de
                cada producto en cada tienda. Decisiones de reposición diarias, listas
                para ejecutar.
              </p>
              <p className="mt-4 text-[17px] md:text-[19px] font-bold leading-[1.3]"
                style={{ color: C.amberDark, fontFamily: SANS, letterSpacing: "-0.01em" }}>
                Menos quiebres. Menos sobrestock. Más margen.
              </p>
            </div>
            <button onClick={() => onNavigate("rumbo")}
              className="flex-shrink-0 rounded-xl text-sm md:text-base px-7 py-3.5 font-bold transition-all hover:-translate-y-0.5"
              style={{ background: C.amber, color: C.navy, fontFamily: SANS, boxShadow: "0 4px 24px rgba(255,200,87,0.35)" }}>
              Explorar RUMBO →
            </button>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════ */
export default function HomePage({ onNavigate, language }) {
  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Playfair+Display:wght@400;700&family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <Hero onNavigate={onNavigate} />
      <SolutionSection onNavigate={onNavigate} />
      <ProblemSection onNavigate={onNavigate} />
    </>
  );
}
